/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"

/* Helpers, constants, custom methods, internal libraries */
import { PAYMENT_ROUTE, TRAVEL_CREDITS_ROUTE } from "../../constants/routes.js";
import { useIsMobile } from "../../hooks/useIsMobile.js";
import { useUserContext } from "../../contexts/userContext";
import { AIRLINES } from "../../constants/appConstants";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useTravelCreditsService } from "services/travelCreditsService.js";

/* Components */
import PaymentsTab from "./paymentsTab/index.js";
import TravelCreditsTab from "./travelCreditsTab/index.js";
import UCSelect from "../ucselect/index.js";

import styles, { tabsSelectOpen} from "./styles.js"
import {storageFactory} from "factory/storageFactory";

const localStore = storageFactory(() => localStorage);


const tabOptions = []

const PaymentTabbedNav = ({addPayment, editPayment, viewPayment, paymentResponseData}) => {
  const location = useLocation();
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const { checkPermission } = useHasPermission();
  const { downloadTravelCredits, initialViewTravelCredits, tripCreditsSettings } = useTravelCreditsService();

  const [defaultPage, setDefaultPage] = useState("payment");

  useEffect(() => {
    setDefaultPage(getDefaultPage())
  }, [location.pathname]);

  useEffect(() => {
    if(checkPermission("F_VIEW_PAYMENTS")) {
      if (tabOptions.findIndex((item) => item.value === "payment") === -1) {
        tabOptions.push({ value: 'payment', label: <FormattedMessage id={`payment.paymentsTab.${providerCode}`} /> })
      }
    }
    if(checkPermission("F_VIEW_TRIPCREDITS")) {
      if (tabOptions.findIndex((item) => item.value === "travelcredits") === -1) {
        tabOptions.push({ value: 'travelcredits', label:
          <div className={classes.totalCreditMobile}>
            <FormattedMessage id="payment.travelCreditsTab" />
            {(parseInt(localStore.getItem("travelCreditsCount")) || 0) > 0 &&
              <p className={classes.totalCredit}>
                {(localStore.getItem("travelCreditsCount") || 0).toString()}
              </p>}
          </div>
        })
      }
    }
  }, []);

  const getDefaultPage = () => {
    if(location.pathname.includes(TRAVEL_CREDITS_ROUTE)) return "travelcredits"
    if(location.pathname.includes(PAYMENT_ROUTE)) return "payment"
  }

  const getDefaultLabel = () => {
    if(location.pathname.includes(TRAVEL_CREDITS_ROUTE)) return <FormattedMessage id="payment.travelCreditsTab"/>
    if(location.pathname.includes(PAYMENT_ROUTE)) return <FormattedMessage id={`payment.paymentsTab.${providerCode}`}/>
  }

  const handleTabChange = async (nextTab) => {
    switch(nextTab) {  
      case "payment":
        navigate('../' + PAYMENT_ROUTE);
      break
      case "travelcredits":
        navigate('../' + TRAVEL_CREDITS_ROUTE);
        if (userDetails?.userInfo?.showTripCreditModal && checkPermission("F_VIEW_TRIPCREDIT_MODAL")) {
          await initialViewTravelCredits()
        }
      break;
    }
  }

  const viewTab = tab => {
    switch(tab) {
      case "payment":
        return checkPermission("F_VIEW_PAYMENTS") && <PaymentsTab addPayment={addPayment} editPayment={editPayment} paymentResponseData={paymentResponseData} providerCode={providerCode }/>
      case "travelcredits":
        return checkPermission("F_VIEW_TRIPCREDITS") && <TravelCreditsTab downloadTravelCredits={downloadTravelCredits} tripCreditsSettings={tripCreditsSettings}/>
    }
  }

  const isSimplePaymentView = [AIRLINES.AS, AIRLINES.AM].includes(providerCode);

  return (
    <div className={classes.paymentTabbedNav}>
      {
        isSimplePaymentView ?
          <div className={classes.alaskaPayment}>
            <PaymentsTab addPayment={addPayment} editPayment={editPayment} viewPayment={viewPayment} paymentResponseData={paymentResponseData} providerCode={providerCode }/>
          </div>
          :
          <>
            {isMobile ? (
              <>
                <UCSelect
                  styles={{ ...tabsSelectOpen }}
                  defaultValue={{
                    value: defaultPage || getDefaultPage(),
                    label: getDefaultLabel()
                  }}
                  value={defaultPage || getDefaultPage()}
                  options={tabOptions}
                  onChange={(tab) => handleTabChange(tab.value)}
                />
                {viewTab(defaultPage || getDefaultPage())}
              </>
            ) : (
              <Tabs
                onSelect={(nextTab) => handleTabChange(nextTab)}
                defaultActiveKey={getDefaultPage()}
                activeKey={defaultPage}
                id="payment-page-tabs"
                className={classes.tabs}
              >
                {
                  checkPermission("F_VIEW_PAYMENTS") &&
                  <Tab eventKey="payment" title={<FormattedMessage id={`payment.paymentsTab.${providerCode}`} />}>
                    {getDefaultPage() === "payment" && <PaymentsTab addPayment={addPayment} editPayment={editPayment} paymentResponseData={paymentResponseData} providerCode={providerCode }/>}
                  </Tab>
                }
                {
                  checkPermission("F_VIEW_TRIPCREDITS") && providerCode === AIRLINES.AA &&
                  <Tab
                    eventKey="travelcredits"
                    title={
                      <>
                        <FormattedMessage id="payment.travelCreditsTab" />
                        {(parseInt(localStore.getItem("travelCreditsCount")) || 0) > 0 &&
                          <p className={classes.totalCredit}>
                            {(localStore.getItem("travelCreditsCount") || 0).toString()}
                          </p>}
                      </>
                    }
                  >
                    {getDefaultPage() === "travelcredits" && <TravelCreditsTab downloadTravelCredits={downloadTravelCredits} tripCreditsSettings={tripCreditsSettings}/>}
                  </Tab>
                }
              </Tabs>
            )}
          </>
      }
    </div>
  )
}
export default PaymentTabbedNav;